function getFocusableElements(container) {
  return Array.from(
    container.querySelectorAll(
      "summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe"
    )
  );
}

class HTMLUpdateUtility {
  /**
   * Used to swap an HTML node with a new node.
   * The new node is inserted as a previous sibling to the old node, the old node is hidden, and then the old node is removed.
   *
   * The function currently uses a double buffer approach, but this should be replaced by a view transition once it is more widely supported https://developer.mozilla.org/en-US/docs/Web/API/View_Transitions_API
   */
  static viewTransition(
    oldNode,
    newContent,
    preProcessCallbacks = [],
    postProcessCallbacks = [],
  ) {
    preProcessCallbacks?.forEach((callback) => callback(newContent));

    const newNodeWrapper = document.createElement("div");
    HTMLUpdateUtility.setInnerHTML(newNodeWrapper, newContent.outerHTML);
    const newNode = newNodeWrapper.firstChild;

    // dedupe IDs
    const uniqueKey = Date.now();
    oldNode.querySelectorAll("[id], [form]").forEach((element) => {
      /* eslint-disable @typescript-eslint/no-unused-expressions */
      element.id && (element.id = `${element.id}-${uniqueKey}`);
      /* eslint-disable @typescript-eslint/no-unused-expressions */
      element.form &&
      element.setAttribute(
        "form",
        `${element.form.getAttribute("id")}-${uniqueKey}`,
      );
    });

    oldNode.parentNode.insertBefore(newNode, oldNode);
    oldNode.style.display = "none";

    postProcessCallbacks?.forEach((callback) => callback(newNode));

    setTimeout(() => oldNode.remove(), 500);
  }

  // Sets inner HTML and reinjects the script tags to allow execution. By default, scripts are disabled when using element.innerHTML.
  static setInnerHTML(element, html) {
    element.innerHTML = html;
    element.querySelectorAll("script").forEach((oldScriptTag) => {
      const newScriptTag = document.createElement("script");
      Array.from(oldScriptTag.attributes).forEach((attribute) => {
        newScriptTag.setAttribute(attribute.name, attribute.value);
      });
      newScriptTag.appendChild(document.createTextNode(oldScriptTag.innerHTML));
      oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag);
    });
  }
}

document.querySelectorAll('[id^="Details-"] summary').forEach((summary) => {
  summary.setAttribute('role', 'button');
  summary.setAttribute('aria-expanded', summary.parentNode.hasAttribute('open'));

  if(summary.nextElementSibling.getAttribute('id')) {
    summary.setAttribute('aria-controls', summary.nextElementSibling.id);
  }

  summary.addEventListener('click', (event) => {
    event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
  });

  if (summary.closest('header-drawer, menu-drawer')) return;
  summary.parentElement.addEventListener('keyup', onKeyUpEscape);
});

const trapFocusHandlers = {};

function trapFocus(container, elementToFocus = container) {
  var elements = getFocusableElements(container);
  var first = elements[0];
  var last = elements[elements.length - 1];

  removeTrapFocus();

  trapFocusHandlers.focusin = (event) => {
    if (
      event.target !== container &&
      event.target !== last &&
      event.target !== first
    )
      return;

    document.addEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.focusout = function() {
    document.removeEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.keydown = function(event) {
    if (event.code.toUpperCase() !== 'TAB') return; // If not TAB key
    // On the last focusable element and tab forward, focus the first element.
    if (event.target === last && !event.shiftKey) {
      event.preventDefault();
      first.focus();
    }

    //  On the first focusable element and tab backward, focus the last element.
    if (
      (event.target === container || event.target === first) &&
      event.shiftKey
    ) {
      event.preventDefault();
      last.focus();
    }
  };

  document.addEventListener('focusout', trapFocusHandlers.focusout);
  document.addEventListener('focusin', trapFocusHandlers.focusin);

  setTimeout(() => {
    elementToFocus.focus();
  })
}

// Here run the querySelector to figure out if the browser supports :focus-visible or not and run code based on it.
try {
  document.querySelector(":focus-visible");
} catch(e) {
  focusVisiblePolyfill();
}

function focusVisiblePolyfill() {
  const navKeys = ['ARROWUP', 'ARROWDOWN', 'ARROWLEFT', 'ARROWRIGHT', 'TAB', 'ENTER', 'SPACE', 'ESCAPE', 'HOME', 'END', 'PAGEUP', 'PAGEDOWN']
  let currentFocusedElement = null;
  let mouseClick = null;

  window.addEventListener('keydown', (event) => {
    if(navKeys.includes(event.code.toUpperCase())) {
      mouseClick = false;
    }
  });

  window.addEventListener('mousedown', (event) => {
    mouseClick = true;
  });

  window.addEventListener('focus', () => {
    if (currentFocusedElement) currentFocusedElement.classList.remove('focused');

    if (mouseClick) return;

    currentFocusedElement = document.activeElement;
    currentFocusedElement.classList.add('focused');

  }, true);
}

function pauseAllMedia() {
  document.querySelectorAll('.js-youtube').forEach((video) => {
    if (video && video.contentWindow) {
      video.contentWindow.postMessage('{"event":"command","func":"' + 'pauseVideo' + '","args":""}', '*');
    }
  });
  document.querySelectorAll('.js-vimeo').forEach((video) => {
    if (video && video.contentWindow) {
      video.contentWindow.postMessage('{"method":"pause"}', '*');
    }
  });
  document.querySelectorAll('video:not([data-autoplay-video-id])').forEach((video) => video.pause());
  document.querySelectorAll('product-model').forEach((model) => {
    if (model.modelViewerUI) model.modelViewerUI.pause();
  });
}

function removeTrapFocus(elementToFocus = null) {
  document.removeEventListener('focusin', trapFocusHandlers.focusin);
  document.removeEventListener('focusout', trapFocusHandlers.focusout);
  document.removeEventListener('keydown', trapFocusHandlers.keydown);

  if (elementToFocus) elementToFocus.focus();
}

function onKeyUpEscape(event) {
  if (event.code.toUpperCase() !== 'ESCAPE') return;

  const openDetailsElement = event.target.closest('details[open]');
  if (!openDetailsElement) return;

  const summaryElement = openDetailsElement.querySelector('summary');
  openDetailsElement.removeAttribute('open');
  summaryElement.setAttribute('aria-expanded', false);
  summaryElement.focus();
}

class QuantityInput extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('input');
    this.changeEvent = new Event('change', { bubbles: true });

    this.input.addEventListener('change', this.onInputChange.bind(this));
    this.querySelectorAll('button').forEach(
      (button) => button.addEventListener('click', this.onButtonClick.bind(this))
    );
  }

  quantityUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.validateQtyRules();
    this.quantityUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.quantityUpdate, this.validateQtyRules.bind(this));
  }

  disconnectedCallback() {
    if (this.quantityUpdateUnsubscriber) {
      this.quantityUpdateUnsubscriber();
    }
  }

  onInputChange(event) {
    this.validateQtyRules();
  }

  onButtonClick(event) {
    event.preventDefault();
    const previousValue = this.input.value;

    event.target.name === 'plus' ? this.input.stepUp() : this.input.stepDown();
    if (previousValue !== this.input.value) this.input.dispatchEvent(this.changeEvent);
  }

  validateQtyRules() {
    const value = parseInt(this.input.value);
    if (this.input.min) {
      const min = parseInt(this.input.min);
      const buttonMinus = this.querySelector(".quantity-button[name='minus']");
      buttonMinus.classList.toggle('disabled', value <= min);
    }
    if (this.input.max) {
      const max = parseInt(this.input.max);
      const buttonPlus = this.querySelector(".quantity-button[name='plus']");
      buttonPlus.classList.toggle('disabled', value >= max);
    }
  }
}

customElements.define('quantity-input', QuantityInput);

function debounce(fn, wait) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}

function throttle(fn, delay) {
  let lastCall = 0;
  return function (...args) {
    const now = new Date().getTime();
    if (now - lastCall < delay) {
      return;
    }
    lastCall = now;
    return fn(...args);
  };
}

function fetchConfig(type = 'json') {
  return {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: `application/${type}` },
  };
}

/*
 * Shopify Common JS
 *
 */
if ((typeof window.Shopify) == 'undefined') {
  window.Shopify = {};
}

Shopify.bind = function(fn, scope) {
  return function() {
    return fn.apply(scope, arguments);
  }
};

Shopify.setSelectorByValue = function(selector, value) {
  for (var i = 0, count = selector.options.length; i < count; i++) {
    var option = selector.options[i];
    if (value == option.value || value == option.innerHTML) {
      selector.selectedIndex = i;
      return i;
    }
  }
};

Shopify.addListener = function(target, eventName, callback) {
  target.addEventListener ? target.addEventListener(eventName, callback, false) : target.attachEvent('on'+eventName, callback);
};

Shopify.postLink = function(path, options) {
  options = options || {};
  var method = options['method'] || 'post';
  var params = options['parameters'] || {};

  var form = document.createElement("form");
  form.setAttribute("method", method);
  form.setAttribute("action", path);

  for(var key in params) {
    var hiddenField = document.createElement("input");
    hiddenField.setAttribute("type", "hidden");
    hiddenField.setAttribute("name", key);
    hiddenField.setAttribute("value", params[key]);
    form.appendChild(hiddenField);
  }
  document.body.appendChild(form);
  form.submit();
  document.body.removeChild(form);
};

Shopify.CountryProvinceSelector = function(country_domid, province_domid, options) {
  this.countryEl         = document.getElementById(country_domid);
  this.provinceEl        = document.getElementById(province_domid);
  this.provinceContainer = document.getElementById(options['hideElement'] || province_domid);

  Shopify.addListener(this.countryEl, 'change', Shopify.bind(this.countryHandler,this));

  this.initCountry();
  this.initProvince();
};

Shopify.CountryProvinceSelector.prototype = {
  initCountry: function() {
    var value = this.countryEl.getAttribute('data-default');
    Shopify.setSelectorByValue(this.countryEl, value);
    this.countryHandler();
  },

  initProvince: function() {
    var value = this.provinceEl.getAttribute('data-default');
    if (value && this.provinceEl.options.length > 0) {
      Shopify.setSelectorByValue(this.provinceEl, value);
    }
  },

  countryHandler: function(e) {
    var opt       = this.countryEl.options[this.countryEl.selectedIndex];
    var raw       = opt.getAttribute('data-provinces');
    var provinces = JSON.parse(raw);

    this.clearOptions(this.provinceEl);
    if (provinces && provinces.length == 0) {
      this.provinceContainer.style.display = 'none';
    } else {
      for (var i = 0; i < provinces.length; i++) {
        var opt = document.createElement('option');
        opt.value = provinces[i][0];
        opt.innerHTML = provinces[i][1];
        this.provinceEl.appendChild(opt);
      }

      this.provinceContainer.style.display = "";
    }
  },

  clearOptions: function(selector) {
    while (selector.firstChild) {
      selector.removeChild(selector.firstChild);
    }
  },

  setOptions: function(selector, values) {
    for (var i = 0, count = values.length; i < values.length; i++) {
      var opt = document.createElement('option');
      opt.value = values[i];
      opt.innerHTML = values[i];
      selector.appendChild(opt);
    }
  }
};

class MenuDrawer extends HTMLElement {
  constructor() {
    super();

    this.mainDetailsToggle = this.querySelector('details');

    this.addEventListener('keyup', this.onKeyUp.bind(this));
    this.bindEvents();
  }

  bindEvents() {
    this.querySelectorAll('summary').forEach((summary) =>
      summary.addEventListener('click', this.onSummaryClick.bind(this))
    );
    this.querySelectorAll('button:not(.localization-selector)').forEach((button) =>
      button.addEventListener('click', this.onCloseButtonClick.bind(this))
    );
  }

  onKeyUp(event) {
    if(event.code.toUpperCase() !== 'ESCAPE') return;

    const openDetailsElement = event.target.closest('details[open]');
    if(!openDetailsElement) return;

    openDetailsElement === this.mainDetailsToggle ? this.closeMenuDrawer(event, this.mainDetailsToggle.querySelector('summary')) : this.closeSubmenu(openDetailsElement);
  }

  onSummaryClick(event) {
    const summaryElement = event.currentTarget;
    const detailsElement = summaryElement.parentNode;
    const parentMenuElement = detailsElement.closest('.has-submenu');
    const isOpen = detailsElement.hasAttribute('open');
    const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)");

    function addTrapFocus() {
      trapFocus(summaryElement.nextElementSibling, detailsElement.querySelector('button'));
      summaryElement.nextElementSibling.removeEventListener('transitionend', addTrapFocus);
    }

    if (detailsElement === this.mainDetailsToggle) {
      if(isOpen) event.preventDefault();
      isOpen ? this.closeMenuDrawer(event, summaryElement) : this.openMenuDrawer(summaryElement);
    } else {
      setTimeout(() => {
        detailsElement.classList.add('menu-opening');
        summaryElement.setAttribute('aria-expanded', true);
        parentMenuElement && parentMenuElement.classList.add('submenu-open');
        !reducedMotion || reducedMotion.matches ? addTrapFocus() : summaryElement.nextElementSibling.addEventListener('transitionend', addTrapFocus);
      }, 100);
    }
  }

  openMenuDrawer(summaryElement) {
    setTimeout(() => {
      this.mainDetailsToggle.classList.add('menu-opening');
    });
    summaryElement.setAttribute('aria-expanded', true);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden`);
  }

  closeMenuDrawer(event, elementToFocus = false) {
    if (event === undefined) return;

    this.mainDetailsToggle.classList.remove('menu-opening');
    this.mainDetailsToggle.querySelectorAll('details').forEach(details => {
      details.removeAttribute('open');
      details.classList.remove('menu-opening');
    });
    this.mainDetailsToggle.querySelectorAll('.submenu-open').forEach(submenu => {
      submenu.classList.remove('submenu-open');
    });
    document.body.classList.remove(`overflow-hidden`);
    removeTrapFocus(elementToFocus);
    this.closeAnimation(this.mainDetailsToggle);
  }

  onCloseButtonClick(event) {
    const detailsElement = event.currentTarget.closest('details');
    this.closeSubmenu(detailsElement);
  }

  closeSubmenu(detailsElement) {
    const parentMenuElement = detailsElement.closest('.submenu-open');
    parentMenuElement && parentMenuElement.classList.remove('submenu-open');
    detailsElement.classList.remove('menu-opening');
    detailsElement.querySelector('summary').setAttribute('aria-expanded', false);
    removeTrapFocus(detailsElement.querySelector('summary'));
    this.closeAnimation(detailsElement);
  }

  closeAnimation(detailsElement) {
    let animationStart;

    const handleAnimation = (time) => {
      if (animationStart === undefined) {
        animationStart = time;
      }

      const elapsedTime = time - animationStart;

      if (elapsedTime < 400) {
        window.requestAnimationFrame(handleAnimation);
      } else {
        detailsElement.removeAttribute('open');
        if (detailsElement.closest('details[open]')) {
          trapFocus(detailsElement.closest('details[open]'), detailsElement.querySelector('summary'));
        }
      }
    }

    window.requestAnimationFrame(handleAnimation);
  }
}

customElements.define('menu-drawer', MenuDrawer);

class HeaderDrawer extends MenuDrawer {
  constructor() {
    super();
  }

  openMenuDrawer(summaryElement) {
    this.header = this.header || document.getElementById('shopify-section-header');
    this.header.classList.add('menu-open');

    setTimeout(() => {
      this.mainDetailsToggle.classList.add('menu-opening');
    });

    summaryElement.setAttribute('aria-expanded', true);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden`);
  }

  closeMenuDrawer(event, elementToFocus) {
    super.closeMenuDrawer(event, elementToFocus);
    this.header.classList.remove('menu-open');
  }
}

customElements.define('header-drawer', HeaderDrawer);

class ModalDialog extends HTMLElement {
  constructor() {
    super();
    this.querySelector('[id^="ModalClose-"]').addEventListener(
      'click',
      this.hide.bind(this, false)
    );
    document.body.addEventListener('keyup', (event) => {
      const isOpenModal = this.hasAttribute('open');
      if (event.code.toUpperCase() === 'ESCAPE' && isOpenModal) this.hide();
    });
    if (this.classList.contains('media-modal')) {
      this.addEventListener('pointerup', (event) => {
        if (event.pointerType === 'mouse' && !event.target.closest('deferred-media, product-model')) this.hide();
      });
    } else {
      this.addEventListener('click', (event) => {
        if (event.target === this) this.hide();
      });
    }
  }

  connectedCallback() {
    if (this.moved) return;
    this.moved = true;
    document.body.appendChild(this);
  }

  show(opener) {
    this.openedBy = opener;
    const popup = this.querySelector('.template-popup');
    document.body.classList.add('overflow-hidden');
    this.setAttribute('open', '');
    if (popup) popup.loadContent();
    trapFocus(this, this.querySelector('[role="dialog"]'));
    window.pauseAllMedia();
  }

  hide() {
    document.body.classList.remove('overflow-hidden');
    document.body.dispatchEvent(new CustomEvent('modalClosed'));
    this.removeAttribute('open');
    removeTrapFocus(this.openedBy);
    window.pauseAllMedia();
  }
}
customElements.define('modal-dialog', ModalDialog);

class ModalOpener extends HTMLElement {
  constructor() {
    super();

    const button = this.querySelector('button:not(.btn-autoplay-video)');

    if (!button) return;
    button.addEventListener('click', () => {
      const modal = document.querySelector(this.getAttribute('data-modal'));
      if (modal) modal.show(button);
    });
  }
}
customElements.define('modal-opener', ModalOpener);

class DeferredMedia extends HTMLElement {
  constructor() {
    super();
    const poster = this.querySelector('[id^="Deferred-Poster-"]');
    if (!poster) return;
    poster.addEventListener('click', this.loadContent.bind(this));
  }

  loadContent(focus = true) {
    window.pauseAllMedia();
    if (!this.getAttribute('loaded')) {
      const content = document.createElement('div');
      content.appendChild(this.querySelector('template').content.firstElementChild.cloneNode(true));

      this.setAttribute('loaded', true);
      const deferredElement = this.appendChild(content.querySelector('video, model-viewer, iframe'));
      if (focus) deferredElement.focus();
    }
  }
}

customElements.define('deferred-media', DeferredMedia);

class SliderComponent extends HTMLElement {
  constructor() {
    super();

    this.slider = this.querySelector('[id^="Slider-"]');
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.enableSliderLooping = false;

    if (this.slider.classList.contains('testimonial-items')) {
      this.prevButton = this.querySelector('button[name="previous"]');
      this.nextButton = this.querySelector('button[name="next"]');
    } else {
      this.prevButton = this.querySelector(
        'button[name="previous-slide"]:not(.gallery-button)',
      );
      this.nextButton = this.querySelector(
        'button[name="next-slide"]:not(.gallery-button)',
      );
    }

    this.dotsContainer = this.querySelector(".slider-dots");
    this.isVertical = this.classList.contains("left-thumbnail");

    if (!this.slider || !this.nextButton) return;

    this.initPages();

    const resizeObserver = new ResizeObserver(() => this.initPages());
    resizeObserver.observe(this.slider);

    this.slider.addEventListener("scroll", this.update.bind(this));
    this.prevButton.addEventListener("click", this.onButtonClick.bind(this));
    this.nextButton.addEventListener("click", this.onButtonClick.bind(this));
  }

  initPages() {
    this.sliderItemsToShow = this.sliderItems;

    if (this.sliderItemsToShow.length < 2) return;

    if (!this.isVertical) {
      this.sliderItemOffset =
        this.sliderItemsToShow[1].offsetLeft -
        this.sliderItemsToShow[0].offsetLeft;
      this.slidesPerPage = Math.floor(
        (this.slider.clientWidth - this.sliderItemsToShow[0].offsetLeft) /
          this.sliderItemOffset,
      );
    }

    if (this.slidesPerPage !== 0) {
      this.totalPages = this.sliderItemsToShow.length - this.slidesPerPage + 1;
    } else {
      this.totalPages = this.sliderItemsToShow.length;
    }

    this.renderDots();
    this.update();
  }

  renderDots() {
    if (!this.dotsContainer) return;
    this.dotsContainer.innerHTML = "";

    for (let i = 1; i < this.totalPages; i++) {
      const dot = document.createElement("button");
      dot.classList.add("slider-dot");
      dot.dataset.index = i;
      dot.setAttribute("aria-label", `Go to slide ${i + 1}`);
      dot.addEventListener("click", this.onDotClick.bind(this));
      this.dotsContainer.appendChild(dot);
    }

    this.updateDots();
  }

  updateDots() {
    if (!this.dotsContainer) return;
    const dots = this.dotsContainer.querySelectorAll(".slider-dot");
    dots.forEach((dot, index) => {
      dot.classList.toggle("is-active", index + 1 === this.currentPage);
    });
  }

  onDotClick(event) {
    const index = parseInt(event.currentTarget.dataset.index, 10) - 1;
    this.slider.scrollTo({
      left: index * this.sliderItemOffset,
      behavior: "smooth",
    });
    this.currentPage = index + 1;

    this.updateDots();
  }

  update() {
    if (!this.slider || !this.nextButton) return;

    const previousPage = this.currentPage;
    const scrollPosition = this.isVertical
      ? this.slider.scrollTop
      : this.slider.scrollLeft;

    this.currentPage = Math.round(scrollPosition / this.sliderItemOffset) + 1;


    if (this.currentPage != previousPage) {
      this.dispatchEvent(
        new CustomEvent("slideChanged", {
          detail: {
            currentPage: this.currentPage,
            currentElement: this.sliderItemsToShow[this.currentPage - 1],
          },
        }),
      );
    }

    if (this.enableSliderLooping) return;

    const firstVisible = this.isSlideVisible(this.sliderItemsToShow[0]);
    const lastVisible = this.isSlideVisible(
      this.sliderItemsToShow[this.sliderItemsToShow.length - 1],
    );

    if (firstVisible && scrollPosition === 0) {
      this.prevButton.setAttribute("disabled", "disabled");
    } else {
      this.prevButton.removeAttribute("disabled");
    }

    if (lastVisible) {
      this.nextButton.setAttribute("disabled", "disabled");
    } else {
      this.nextButton.removeAttribute("disabled");
    }
  }

  isSlideVisible(element, offset = 0) {
    const lastVisibleSlide = this.isVertical
      ? this.slider.clientHeight + this.slider.scrollTop - offset
      : this.slider.clientWidth + this.slider.scrollLeft - offset;

    return this.isVertical
      ? element.offsetTop + element.clientHeight <= lastVisibleSlide &&
          element.offsetTop >= this.slider.scrollTop
      : element.offsetLeft + element.clientWidth <= lastVisibleSlide &&
          element.offsetLeft >= this.slider.scrollLeft;
  }

  onButtonClick(event) {
    event.preventDefault();
    const step = event.currentTarget.dataset.step || 1;
    this.slideScrollPosition =
      event.currentTarget.name === "next"
        ? (this.isVertical ? this.slider.scrollTop : this.slider.scrollLeft) +
          step * this.sliderItemOffset
        : (this.isVertical ? this.slider.scrollTop : this.slider.scrollLeft) -
          step * this.sliderItemOffset;

    this.setSlidePosition(this.slideScrollPosition);
  }

  setSlidePosition(position) {
    this.slider.scrollTo({
      [this.isVertical ? "top" : "left"]: position,
    });
  }
}

customElements.define('slider-component', SliderComponent);

class VariantSelects extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.addEventListener("change", (event) => {
      const target = this.getInputForEventTarget(event.target);
      const selectedVariantId = target.dataset.optionVariantId;

      const productCard = event.target.closest(".product-card-wrapper");

      if (productCard) {
        // Update variant id
        const hiddenInput = productCard.querySelector(".product-variant-id");
        if (hiddenInput && selectedVariantId) {
          hiddenInput.value = selectedVariantId;
        }

        // Update variant image
        const variantImages = productCard.querySelectorAll(".variant-image");
        variantImages.forEach((image) => image.classList.remove("active")); // Remove 'active' from all images

        const selectedImage = productCard.querySelector(
          `.variant-image[data-variant-id="${selectedVariantId}"]`,
        );
        if (selectedImage) {
          selectedImage.classList.add("active"); // Add 'active' to the selected image
        }
      }

      this.updateSelectionMetadata(event);

      publish(PUB_SUB_EVENTS.optionValueSelectionChange, {
        data: {
          event,
          target,
          selectedOptionValues: this.selectedOptionValues,
        },
      });
    });
  }

  updateSelectionMetadata({ target }) {
    const { value, tagName } = target;

    if (tagName === "SELECT" && target.selectedOptions.length) {
      Array.from(target.options)
        .find((option) => option.getAttribute("selected"))
        .removeAttribute("selected");
      target.selectedOptions[0].setAttribute("selected", "selected");

      const swatchValue = target.selectedOptions[0].dataset.optionSwatchValue;
      const selectedDropdownSwatchValue = target
        .closest(".product-form__input")
        .querySelector("[data-selected-value] > .swatch");
      if (!selectedDropdownSwatchValue) return;
      if (swatchValue) {
        selectedDropdownSwatchValue.style.setProperty(
          "--swatch--background",
          swatchValue,
        );
        selectedDropdownSwatchValue.classList.remove("swatch--unavailable");
      } else {
        selectedDropdownSwatchValue.style.setProperty(
          "--swatch--background",
          "unset",
        );
        selectedDropdownSwatchValue.classList.add("swatch--unavailable");
      }

      selectedDropdownSwatchValue.style.setProperty(
        "--swatch-focal-point",
        target.selectedOptions[0].dataset.optionSwatchFocalPoint || "unset",
      );
    } else if (tagName === "INPUT" && target.type === "radio") {
      const selectedSwatchValue = target
        .closest(`.product-form__input`)
        .querySelector("[data-selected-value]");

      if (selectedSwatchValue) selectedSwatchValue.innerHTML = value;
    }
  }

  getInputForEventTarget(target) {
    return target.tagName === "SELECT" ? target.selectedOptions[0] : target;
  }

  get selectedOptionValues() {
    return Array.from(
      this.querySelectorAll("select option[selected], fieldset input:checked"),
    ).map(({ dataset }) => dataset.optionValueId);
  }
}

customElements.get("variant-selects") ||
customElements.define("variant-selects", VariantSelects);
class VariantRadios extends VariantSelects {
  constructor() {
    super();

    // Status Classes
    this.statusClasses = {
      unavailable: 'unavailable',
      outOfStock: 'out-of-stock',
    }

    // Swatches
    this.swatches = /** @type {HTMLInputElement[]} */ (Array.from(this.querySelectorAll('input[type="radio"]')));

    // Prioritize color as the initial swatch that is "clicked" onLoad, or fallback to the first option type.
    const colorIndex = this.productOptions.findIndex(option => option.toLowerCase() === 'color') + 1;
    const priorityOptionIndex = colorIndex ? colorIndex : 1;

    // Set initial swatches state when the page loads
    this.intialSwatchState(priorityOptionIndex);
  }

  /**
   * Pull the values from currently selected swatches (product options) in <variant-radios>
   */
   updateOptions() {
    // Without this, currentVariant doesn't change on selection
    const fieldsets = Array.from(this.querySelectorAll('fieldset'));

    // Update it with the currently checked options
    this.options = fieldsets.map((fieldset, index) => {
      const optionInputs = Array.from(fieldset.querySelectorAll('input'));
      const checkedOptionInput = optionInputs.find(radio => radio.checked);

      // Iterate over all optionInputs to update the checked attribute accordingly
      optionInputs.forEach(input => {
        // If this input is the checked one, ensure it's marked as checked
        if (input.checked = checkedOptionInput && input === checkedOptionInput) {
          input.checked = true;
        } else {
          // Otherwise, ensure it is not marked as checked
          input.checked = false;
        }
      });

      // Return the value of the checked input, if any
      return checkedOptionInput ? checkedOptionInput.value : null;
    });
  }

  /**
   * Event Handler: Change
   * @param {Object} event The change event interface emitted from to the radio button's event listener.
   * @see https://developer.mozilla.org/en-US/docs/Web/API/HTMLElement/change_event
   */
  onVariantChange(event) {
    // Update currently selected product options that the methods of this component reference internally
    this.updateOptions();

    // Update the currently selected variant id that the methods of this component reference internally
    this.updateMasterId();

    // Update the browsers URL to the newly selected variant
    this.updateURL();

    // Update variant inventory
    this.getQty();

    // Update variant shipping message
    this.updateShippingMessage();

    // AJAX request to refresh content using the section rendering API.
    //this.refreshProductDetails();

    // Update <product-form> with the correct values for the selected variant, ensuring "Add to Cart" adds the correct variant/options
    this.updateVariantInput();

    // Update the <media-gallery> web component
    this.updateMedia();

    // Update the swatch-option-label to display the name of the selected swatch in a group of swatches
    this.updateVariantLabel();

    // Update the state of the swatch images to show availability
    this.updateSwatchState(event);

    // Emit the newly selected variant to other web components on the PDP page
    this.emitVariantChange({
      ...this.currentVariant,
      statusClasses: this.statusClasses
    });
  }

  /**
   * Emits the variantChange event
   *
   * When a user changes the variant by choosing a swatch, this event fires to trigger updates in other web components on the page.
   * Other web components have event listeners set-up that listen for this variantChange event, so each web component is responsible for updating itself.
   * @param {Object} detail The detail object can contain additional data that other web components can use.
   * @event variantChange
   * @see https://developer.mozilla.org/en-US/docs/Web/API/CustomEvent/detail
   */
  emitVariantChange(detail) {
    this.dispatchEvent(new CustomEvent('variantChange', {detail}));
  }

  /**
   * Update the state of all swatches after a variant change
   *
   * This function is responsible for indicating if a swatch is unavailable or out of stock. The swatches
   * are radio buttons in the DOM, modified to appear as selectable swatches.
   *
   * @param {Object} event The change event interface emitted from to the radio button's event listener.
   * @see https://developer.mozilla.org/en-US/docs/Web/API/HTMLElement/change_event
   */
  updateSwatchState(event) {
    // Reset swatch state
    this.resetSwatchesState();

    // Which fieldset of radio buttons was changed?
    // Corresponds with the indexes in the this.options array below.
    const clickedOptionIndex = event.target.dataset.optionIndex;
    const clickedOptionValue = event.target.value;

    // Values of the on elements for all options that are now :checked, after the change event has fired.
    const currentlySelectedOptions = this.options;

    // The current options selected, but with the clicked option removed. For example, if the user clicked "Color", this will include the currently selected values for everything except color.
    const otherSelectedOptions = currentlySelectedOptions.filter(option => option === clickedOptionValue);

    // If a swatch is not available for all variants, add the outOfStock class to that swatch.
    this.swatches.forEach(swatch => {
        const swatchInStock = this.variantData.filter(variant => variant.options.includes(swatch.value) && variant.available);
        if(!swatchInStock.length) {
          swatch.classList.add(this.statusClasses.outOfStock);
        }
    });

    // Availability of all swatches outside of clicked swatch group
    this.swatches
    .filter(swatch => swatch.dataset.optionIndex !== clickedOptionIndex)
    .forEach(swatch => {
      otherSelectedOptions.forEach(option => {
        const optionVariants = this.variantData.filter(variant => variant.options.includes(option) && variant.options.includes(swatch.value) && variant.available);
        if(!optionVariants.length) {
          swatch.classList.add(this.statusClasses.outOfStock);
        }
      });
    });
  }

  /**
   * Re-renders the product details section when the variant changes on the PDP page
   * @see https://shopify.dev/api/section-rendering
   */

   refreshProductDetails() {
    const productDetailsSection = document.querySelector('.product-details-wrapper');
    if(productDetailsSection !== null && typeof productDetailsSection.id === 'string' && this.currentVariant !== undefined && typeof this.currentVariant.id === 'number') {
      fetch(`${window.location.pathname}?variant=${this.currentVariant.id}&section_id=${productDetailsSection.getAttribute('data-section')}`)
      .then((response) => response.text())
      .then((responseText) => {
        const html = new DOMParser().parseFromString(responseText, 'text/html')
        const destination = document.getElementById(productDetailsSection.id);
        const source = html.getElementById(productDetailsSection.id);
        if (source && destination) {
          destination.innerHTML = source.innerHTML;
        }
      });
    }
  }

  getSelectedVariantShippingInfo() {
    // Get the shipping info for the selected variant from your variant data
    const selectedVariant = this.variantData.find((variant) => variant.id === this.currentVariant.id);

    if (selectedVariant && selectedVariant.metafields && selectedVariant.metafields) {
      return selectedVariant.metafields.shipping_info;
    }
  }

  getSelectedVariantQuantity() {
    // Get the variant quantity from json data
    const selectedVariant = this.variantData.find((variant) => variant.id === this.currentVariant.id);

    if (selectedVariant && selectedVariant.variantQuantity && selectedVariant.variantQuantity) {
      return selectedVariant.variantQuantity.quantity;
    }
  }

  /**
   * Initial swatch state
   *
   * When this web component is constructed, set the initial availability state based on the currently selected options.
   *
   * This method fires a "mock" event as though the user clicked on a swatch, it will pull the data for the :checked swatch
   * for the provided optionIndex, and fire the change event as if that swatch were clicked to update the availability of
   * the other swatches on page load.
   *
   * @param {Number} optionIndex The option position/index for the fieldset of swatches you want to "mock" the change event for.
   * @see https://shopify.dev/api/liquid/objects/product_option#product_option-position
   */
  intialSwatchState(optionIndex) {
    // Intial data set-up
    this.updateOptions();
    this.updateMasterId();
    // Fire mock change event to trigger standard variantChange logic
    const mockEvent = {
      target: {
        dataset: {
          optionIndex: optionIndex.toString()
        },
        value: this.options[optionIndex - 1].toString()
      }
    };
    this.updateSwatchState(mockEvent);
  }

  /**
   * Removes all status classes from every swatch, such as out of stock or unavailable classes.
   */
  resetSwatchesState() {
    this.swatches.forEach(swatch => {
      Object.values(this.statusClasses).forEach(status => swatch.classList.remove(status));
    })
  }
}

customElements.define('variant-radios', VariantRadios);

/**
 * PDP Price
 *
 * The <pdp-price> web component is responsible for rendering prices on the PDP page. More specifically,
 * it has event listeners automatically binded to it in order to respond to the 'variantChange' event that
 * fires when the <variant-radios> component changes the currently selected variant on the PDP page.
 *
 * @extends HTMLElement
 */
class PDPPrice extends HTMLElement {
  constructor() {
    super();
    // Set-up event listener to listen for change event in <variant-radios>
    const variantRadios = document.getElementById(`VariantRadios-${this.dataset.sectionId}`)
    if(variantRadios) {
      variantRadios.addEventListener('variantChange', this.onVariantChange.bind(this))
    }
    this.getCurrencyData();
    this.salePrice = this.querySelectorAll('.price-item.price-item-sale');
    this.regularPrice = this.querySelectorAll('.price-item.price-item-regular');
    this.proDiscount = this.currencyData.pro_discount && !Number.isNaN(Number(this.currencyData.pro_discount)) && Number(this.currencyData.pro_discount) < 1 ? Number(this.currencyData.pro_discount) : 1;
    this.proCustomer = this.proDiscount < 1;
    this.eligibleForProDiscount = this.currencyData.product_eligible_for_pro_discount === 'true' && this.currencyData.show_pro_discount === 'true';
   }

  /**
   * variantChange event handler
   * @param {Object} e The variantChange custom event interface emitted from the <variant-radios> web component.
   * @see https://developer.mozilla.org/en-US/docs/Web/API/CustomEvent
   */
  onVariantChange(e) {
    const {compare_at_price, price} = e.detail;
    const on_sale = compare_at_price !== null && compare_at_price > price;
    const regular_price = on_sale ? compare_at_price : price;
    // Update all sale price elements
    this.salePrice.forEach(salePrice => {
      salePrice.innerText = this.proCustomer && this.eligibleForProDiscount ? this.formatPrice(regular_price * this.proDiscount) : this.formatPrice(price);
    });
    // Update all regular price elements
    this.regularPrice.forEach(regularPrice => {
      regularPrice.innerText = this.formatPrice(on_sale ? compare_at_price : price);
    });
    // Set price class on <pdp-price> element to display on sale pricing or regular pricing
    this.setPriceClasses(e.detail)
  }

  /**
   * Adds the correct classes to the price wrapper based on whether or not the product is on sale.
   * This will show/hide the strikethrough price based on which class gets set.
   * @param {Object} variant The shopify variant object passed in the detail property in the 'variantChange' event.
   * @see https://shopify.dev/api/liquid/objects/variant
   */
  setPriceClasses(variant) {
    // Sold Out
    if(variant.available === false) {
      this.classList.add('price-sold-out');
    } else {
      this.classList.remove('price-sold-out');
    }
    // On Sale
    const on_sale = variant.compare_at_price !== null && variant.compare_at_price > variant.price;
    if(on_sale) {
      this.classList.add('price-on-sale');
    } else if(this.proCustomer === true && this.eligibleForProDiscount === true) {
      this.classList.add('price-on-sale');
    } else {
      this.classList.remove('price-on-sale');
    }
  }

  /**
   * Get currency and money format data
   * @returns {Object} The JSON passed in the #json-currency-data script tag inside of the <pdp-price> web component.
   */
  getCurrencyData() {
    this.currencyData = this.currencyData || JSON.parse(this.querySelector('#json-currency-data[type="application/json"]').textContent);
    return this.currencyData;
  }

  /**
   * Format a price string
   * @param {Number|String} price Should be product.price or product.compare_at_price
   * @see https://shopify.dev/api/liquid/objects/product#product-compare_at_price
   * @see https://shopify.dev/api/liquid/objects/product#product-price
   * @see https://shopify.dev/api/liquid/filters/money
   * @see https://shopify.dev/api/liquid/filters/money_with_currency
   * @returns {String} A formatted price string that has shopifys money format filters applied to it.
   */
  formatPrice(price) {
    const {currency_code_enabled, money_format, money_with_currency_format} = this.currencyData;
    const options = {
      style: 'decimal',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    };
    const formatted_price = new Intl.NumberFormat(window.Shopify.locale, options).format(Number(price) / 100);
    const shopify_format = currency_code_enabled ? money_with_currency_format : money_format;
    return shopify_format.replace('{{amount}}', formatted_price);
  }
}

customElements.define('pdp-price', PDPPrice);

class BulkAdd extends HTMLElement {
  constructor() {
    super();
    this.queue = [];
    this.requestStarted = false;
    this.ids = [];
  }

  startQueue(id, quantity) {
    this.queue.push({ id, quantity });
    const interval = setInterval(() => {
      if (this.queue.length > 0) {
        if (!this.requestStarted) {
          this.sendRequest(this.queue);
        }
      } else {
        clearInterval(interval);
      }
    }, 250);
  }

  sendRequest(queue) {
    this.requestStarted = true;
    const items = {};
    queue.forEach((queueItem) => {
      items[parseInt(queueItem.id)] = queueItem.quantity;
    });
    this.queue = this.queue.filter((queueElement) => !queue.includes(queueElement));
    const quickBulkElement = this.closest('quick-order-list') || this.closest('quick-add-bulk');
    quickBulkElement.updateMultipleQty(items);
  }

  resetQuantityInput(id) {
    const input = this.querySelector(`#Quantity-${id}`);
    input.value = input.getAttribute('value');
    this.isEnterPressed = false;
  }

  setValidity(event, index, message) {
    event.target.setCustomValidity(message);
    event.target.reportValidity();
    this.resetQuantityInput(index);
    event.target.select();
  }

  validateQuantity(event) {
    const inputValue = parseInt(event.target.value);
    const index = event.target.dataset.index;

    if (inputValue < event.target.dataset.min) {
      this.setValidity(event, index, window.quickOrderListStrings.min_error.replace('[min]', event.target.dataset.min));
    } else if (inputValue > parseInt(event.target.max)) {
      this.setValidity(event, index, window.quickOrderListStrings.max_error.replace('[max]', event.target.max));
    } else if (inputValue % parseInt(event.target.step) != 0) {
      this.setValidity(event, index, window.quickOrderListStrings.step_error.replace('[step]', event.target.step));
    } else {
      event.target.setCustomValidity('');
      event.target.reportValidity();
      this.startQueue(index, inputValue);
    }
  }

  getSectionsUrl() {
    if (window.pageNumber) {
      return `${window.location.pathname}?page=${window.pageNumber}`;
    } else {
      return `${window.location.pathname}`;
    }
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }
}

if (!customElements.get('bulk-add')) {
  customElements.define('bulk-add', BulkAdd);
}